import { useMemo } from 'react';
import { differenceInHours, parseISO, isAfter, isBefore, addHours } from 'date-fns';
import type { Trip, ItineraryDay } from '../types';

export type TripNotification = {
  id: string;
  type: 'flight' | 'hotel' | 'booking';
  title: string;
  subtitle: string;
  hoursUntil: number;
  urgent: boolean; // < 12h
};

export function useTripNotifications(trip: Trip, days?: ItineraryDay[]): TripNotification[] {
  return useMemo(() => {
    const now = new Date();
    const window = addHours(now, 48);
    const notifications: TripNotification[] = [];

    // Flights
    for (const flight of trip.flights ?? []) {
      const dep = new Date(flight.departureTime);
      if (isAfter(dep, now) && isBefore(dep, window)) {
        const h = differenceInHours(dep, now);
        notifications.push({
          id: `flight-${flight.id}`,
          type: 'flight',
          title: `${flight.direction === 'OUTBOUND' ? 'Outbound' : 'Return'} flight${flight.flightNumber ? ` ${flight.flightNumber}` : ''}`,
          subtitle: `${flight.departureAirport} → ${flight.arrivalAirport} · in ${h}h`,
          hoursUntil: h,
          urgent: h < 12,
        });
      }
    }

    // Hotels — check-in
    for (const hotel of trip.hotels ?? []) {
      const checkIn = new Date(hotel.checkIn);
      if (isAfter(checkIn, now) && isBefore(checkIn, window)) {
        const h = differenceInHours(checkIn, now);
        notifications.push({
          id: `hotel-in-${hotel.id}`,
          type: 'hotel',
          title: `Check-in: ${hotel.name}`,
          subtitle: `in ${h}h${hotel.address ? ` · ${hotel.address}` : ''}`,
          hoursUntil: h,
          urgent: h < 12,
        });
      }
    }

    // Sort by soonest
    return notifications.sort((a, b) => a.hoursUntil - b.hoursUntil);
  }, [trip, days]);
}
